/**
 * Axios instance configured for Orval-generated API hooks.
 * Handles authentication, token refresh, and request/response interceptors.
 * Supports acting-as AI agent users via X-Acting-As-AI-Agent header.
 * Detects network errors for backend availability status.
 *
 * Token Refresh Strategy:
 * - Proactive: Before each request, check if token expires within 60s and refresh preemptively
 * - Reactive: If a 401 still occurs (clock skew, server-side revocation), refresh and retry
 * - Single refresh at a time: All concurrent requests queue and wait for one refresh
 */
import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, isCapacitor } from './config';
import { DEFAULT_REQUEST_TIMEOUT_MS } from './requestTimeout';
import { serializeQueryParams } from './paramsSerializer';
import { getActingAsHeaderValue } from '../contexts/ActingAsContext';
import { getActiveCompanyHeaderValue } from '../contexts/ActiveCompanyContext';
import { isBackendUnavailableError } from '../contexts/ApiStatusContext';
import {
  clearEmulationData,
  endExpiredEmulation,
  isEmulationTeardownStarted,
} from '../contexts/EmulationContext';
import { clearDemoMode } from '../auth/demoSession';
// Leaf modules — importing the auth session keys and the token/refresh
// primitives from here (rather than from AuthContext, which imports THIS file)
// keeps the graph acyclic. `tokenRefresh` is shared with the auth bootstrap so
// both transports apply the same expiry rule and join the same exchange.
import { AUTH_STORAGE_KEYS } from '../auth/storageKeys';
import { clearRefreshToken } from '../auth/secureTokenStore';
import {
  decodeJwtPayload,
  isEmulationAccessToken,
  isTokenExpiringSoon,
  refreshAccessTokens,
} from '../auth/tokenRefresh';

const STORAGE_KEYS = {
  ...AUTH_STORAGE_KEYS,
  // External portal users use different token keys
  externalAccessToken: 'external_access_token',
  externalRefreshToken: 'external_refresh_token',
} as const;


// Custom events
export const SESSION_EXPIRED_EVENT = 'panluma:session-expired';
export const VERSION_MISMATCH_EVENT = 'panluma:version-mismatch';
export const BACKEND_UNAVAILABLE_EVENT = 'panluma:backend-unavailable';
export const REQUEST_SUCCESS_EVENT = 'panluma:request-success';
export const API_HTML_RESPONSE_EVENT = 'panluma:api-html-response';

export const emitApiHtmlResponse = () => {
  window.dispatchEvent(new CustomEvent(API_HTML_RESPONSE_EVENT));
};

export const emitSessionExpired = () => {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
};

export const resetSessionExpiredGuard = () => {
  sessionExpiredEmitted = false;
};

export const emitBackendUnavailable = () => {
  window.dispatchEvent(new CustomEvent(BACKEND_UNAVAILABLE_EVENT));
};

export const emitRequestSuccess = () => {
  window.dispatchEvent(new CustomEvent(REQUEST_SUCCESS_EVENT));
};

// Frontend build version (injected at build time via env var)
const FRONTEND_BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION || 'local';
let versionMismatchDetected = false;
let versionLogged = false;

// Log frontend version on startup for debugging
console.log('[Version] Frontend build:', FRONTEND_BUILD_VERSION);

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    'X-PanLuma-Client': isCapacitor ? 'mobile' : 'web',
  },
  // Repeatable query params (`?status=a&status=b`) — axios would otherwise emit
  // `status[]=a`, a key FastAPI has no parameter for, so the filter binds None and
  // vanishes behind a 200. See shared/api/paramsSerializer.ts.
  paramsSerializer: serializeQueryParams,
});

/**
 * Perform token refresh. Returns true if successful, false otherwise.
 *
 * The exchange itself lives in `shared/auth/tokenRefresh` so the auth bootstrap
 * shares this instance's single-flight promise: a cold load that refreshes from
 * both places performs one rotation, not two racing ones. This wrapper adds the
 * fail-closed half — for a request in flight, any failure ends the session.
 */
async function refreshTokens(): Promise<boolean> {
  // An emulation ended in this page because its token died: the sysadmin's own
  // session has already been restored in storage and a reload is under way.
  // Refuse without clearing anything — handleSessionExpired() here would wipe
  // the session we just restored.
  if (isEmulationTeardownStarted()) {
    return false;
  }

  try {
    await refreshAccessTokens();
    sessionExpiredEmitted = false;
    return true;
  } catch {
    handleSessionExpired();
    return false;
  }
}

/** Check if an access token is already dead (as opposed to merely expiring soon). */
function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return payload.exp <= Math.floor(Date.now() / 1000);
}

/**
 * Decide what an expiring/expired EMULATION access token means for a request.
 *
 * `endExpiredEmulation()` is the one defined outcome for a dead emulation token:
 * an emulation has no refresh token of its own — `STORAGE_KEYS.refreshToken`
 * still holds the sysadmin's — so refreshing instead would silently resume the
 * sysadmin's session behind a still-showing emulation banner (see
 * `isEmulationAccessToken` in shared/auth/tokenRefresh). It also raises the
 * tear-down latch `refreshTokens()` above reads.
 *
 * Returns true when the emulation is over and the caller must abandon the
 * request; false when the token is still usable and should go out as-is.
 */
function resolveExpiringEmulationToken(token: string): boolean {
  if (!isTokenExpired(token)) return false;
  endExpiredEmulation();
  return true;
}

/**
 * Handle session expiration - clear tokens and notify app.
 * Guarded so concurrent 401s only trigger one session-expired event.
 */
let sessionExpiredEmitted = false;
function handleSessionExpired() {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.user);
  // Stays synchronous by contract — this runs inside the interceptors and from
  // `getValidAccessToken`, neither of which can await. `clearRefreshToken()`
  // raises its cleared latch before it returns, so every subsequent read
  // answers null from this instant even though the keystore delete is still in
  // flight; the promise is fire-and-forget by design and never rejects.
  void clearRefreshToken();
  clearEmulationData();
  clearDemoMode();
  if (!sessionExpiredEmitted) {
    sessionExpiredEmitted = true;
    emitSessionExpired();
  }
}

/**
 * Per-request opt-out of the auth interceptor, for endpoints that are public by
 * design (pre-auth screens: register, verify-email, locale defaults).
 *
 * Those callers may still have a STALE token in localStorage from an earlier
 * session. Attaching it makes the interceptor proactively refresh it, and a
 * failed refresh calls `handleSessionExpired()` — wiping storage and firing
 * SESSION_EXPIRED — on a request that never needed a token at all. `skipAuth`
 * sends the request unauthenticated instead, while keeping the shared timeout,
 * error envelope and version handling.
 */
export interface PanLumaRequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
}

// Request interceptor - proactive token refresh before requests
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig & { skipAuth?: boolean }) => {
    if (config.skipAuth) {
      return config;
    }

    // Check for main app token first, then fall back to external portal token
    let token = localStorage.getItem(STORAGE_KEYS.accessToken);
    const isExternalToken = !token;
    if (!token) {
      token = localStorage.getItem(STORAGE_KEYS.externalAccessToken);
    }

    // Proactive refresh if token is expiring soon (only for main app tokens)
    if (token && !isExternalToken && isTokenExpiringSoon(token)) {
      if (isEmulationAccessToken(token)) {
        // Emulation: never exchange the sysadmin's refresh token. A token that
        // is only inside the 60s window is still valid — send it as-is.
        if (resolveExpiringEmulationToken(token)) {
          return Promise.reject(new Error('Emulation session expired'));
        }
      } else {
        const success = await refreshTokens();
        if (!success) {
          return Promise.reject(new Error('Session expired'));
        }
        token = localStorage.getItem(STORAGE_KEYS.accessToken);
      }
    }

    // Add auth header
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add acting-as header if in acting-as mode (only for internal users)
    if (!isExternalToken) {
      const actingAsUserId = getActingAsHeaderValue();
      if (actingAsUserId && config.headers) {
        config.headers['X-Acting-As-AI-Agent'] = actingAsUserId;
      }
      // Multi-company P2: active company filter (UI list-filter hint)
      const activeCompanyId = getActiveCompanyHeaderValue();
      if (activeCompanyId && config.headers) {
        config.headers['X-Active-Company-Id'] = activeCompanyId;
      }
    }

    // Let axios auto-set Content-Type for FormData (multipart/form-data with boundary)
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - reactive 401 handling as fallback
axiosInstance.interceptors.response.use(
  (response) => {
    // Detect HTML responses that should be JSON - this happens when CDN/proxy
    // intercepts errors and returns the SPA HTML page instead of the API response
    const contentType = response.headers['content-type'] || '';
    const isHtmlResponse = contentType.includes('text/html');
    const isApiRequest = response.config.url?.includes('/api/');

    // Skip this check when the caller explicitly requested a non-JSON response type
    const expectsJson = !response.config.responseType || response.config.responseType === 'json';

    if (isHtmlResponse && isApiRequest && expectsJson) {
      console.error('[API Error] Received HTML instead of JSON - likely a proxy/CDN configuration issue', {
        url: response.config.url,
        method: response.config.method?.toUpperCase(),
        status: response.status,
        contentType,
      });
      emitApiHtmlResponse();
      // Throw an error so the mutation/query fails properly
      const error = new Error('Received HTML response instead of JSON. This may indicate a server configuration issue.');
      (error as Error & { isHtmlResponse: boolean }).isHtmlResponse = true;
      throw error;
    }

    emitRequestSuccess();

    // Check for version mismatch - only prompt refresh when frontend is OLDER than backend.
    // During deployment, frontend deploys first (S3 is fast) while backend takes longer (ECS rolling update).
    // A newer frontend talking to an older backend is expected and should NOT trigger refresh.
    // We only want to refresh when the backend has been updated and the frontend is stale.
    const backendVersion = response.headers['x-api-version'];
    if (!versionLogged) {
      versionLogged = true;
      // Log version check on first API response for debugging
      console.log('[Version] Backend:', backendVersion || 'not exposed', '| Frontend:', FRONTEND_BUILD_VERSION);
    }

    // Check if we need version mismatch handling
    const isProductionVersions = backendVersion && backendVersion !== 'local' && FRONTEND_BUILD_VERSION !== 'local';

    if (isProductionVersions && backendVersion === FRONTEND_BUILD_VERSION) {
      // Versions match - clear any stored initial version (deployment complete or successful refresh)
      sessionStorage.removeItem('initialBackendVersion');
    } else if (isProductionVersions && backendVersion !== FRONTEND_BUILD_VERSION && !versionMismatchDetected) {
      // Store the backend version we first saw - only trigger refresh if backend changes again
      // This handles the case where frontend is newer (just deployed) and backend is older (still deploying)
      const storedBackendVersion = sessionStorage.getItem('initialBackendVersion');
      if (!storedBackendVersion) {
        // First time seeing a mismatch - store the backend version and don't refresh yet
        // The frontend is likely newer than the backend (just deployed)
        sessionStorage.setItem('initialBackendVersion', backendVersion);
        console.log('[Version] Mismatch detected but frontend may be newer. Storing initial backend version:', backendVersion);
      } else if (storedBackendVersion !== backendVersion) {
        // Backend version changed from what we initially saw - this means backend was updated
        // and our frontend is now stale. Time to refresh.
        versionMismatchDetected = true;
        console.log('[Version] Backend updated! Was:', storedBackendVersion, 'Now:', backendVersion, '| Frontend:', FRONTEND_BUILD_VERSION);
        sessionStorage.removeItem('initialBackendVersion');
        window.dispatchEvent(
          new CustomEvent(VERSION_MISMATCH_EVENT, {
            detail: { frontendVersion: FRONTEND_BUILD_VERSION, backendVersion },
          })
        );
      }
      // If storedBackendVersion === backendVersion, we already know about this mismatch
      // and are waiting for backend to update (or user to manually refresh)
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      skipAuth?: boolean;
    };

    // Handle 401 - try refresh once as fallback (e.g., server revoked token).
    // A `skipAuth` request sent no token, so its 401 says nothing about the
    // stored session: refreshing (and expiring it on failure) would sign the
    // user out over a public endpoint's own rejection.
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.skipAuth) {
      originalRequest._retry = true;

      // A 401 carrying an emulation access token means the emulation is over —
      // refreshing would exchange the SYSADMIN's refresh token and retry the
      // request as them, against the emulated tenant's data.
      const currentToken = localStorage.getItem(STORAGE_KEYS.accessToken);
      if (currentToken && isEmulationAccessToken(currentToken)) {
        endExpiredEmulation();
        return Promise.reject(error);
      }

      const success = await refreshTokens();
      if (success) {
        // Retry with new token
        const newToken = localStorage.getItem(STORAGE_KEYS.accessToken);
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return axiosInstance(originalRequest);
      }
      // Refresh failed - error already handled in refreshTokens()
      return Promise.reject(error);
    }

    // Handle 503 (backend scaled to zero)
    if (isBackendUnavailableError(error)) {
      emitBackendUnavailable();
    }

    // Log other errors for debugging
    if (error.response?.status && error.response.status >= 400 && error.response.status !== 401) {
      const responseData = error.response.data as {
        detail?: { code?: string; message?: string; error_id?: string };
      } | undefined;
      const detail = responseData?.detail;
      console.warn('[API Error]', {
        status: error.response.status,
        code: detail?.code || 'UNKNOWN',
        message: detail?.message || error.message,
        errorId: detail?.error_id,
        path: originalRequest.url,
        method: originalRequest.method?.toUpperCase(),
      });
    }

    return Promise.reject(error);
  }
);

/**
 * Get a valid access token, refreshing proactively if it's expiring soon.
 * Use this for non-axios requests (e.g., streaming fetch/SSE) that bypass interceptors.
 */
export async function getValidAccessToken(): Promise<string> {
  let token = localStorage.getItem(STORAGE_KEYS.accessToken);
  if (!token) {
    const external = localStorage.getItem(STORAGE_KEYS.externalAccessToken);
    if (external) return external;
    handleSessionExpired();
    throw new Error('No access token available');
  }
  if (isTokenExpiringSoon(token)) {
    // Same emulation guard as the request interceptor — this path bypasses it.
    if (isEmulationAccessToken(token)) {
      if (resolveExpiringEmulationToken(token)) {
        throw new Error('Emulation session expired');
      }
      return token;
    }
    const success = await refreshTokens();
    if (!success) {
      throw new Error('Session expired');
    }
    token = localStorage.getItem(STORAGE_KEYS.accessToken);
    if (!token) {
      throw new Error('No access token after refresh');
    }
  }
  return token;
}

/**
 * Custom instance function for Orval to use
 */
export const customInstance = async <T>(config: AxiosRequestConfig): Promise<T> => {
  const response = await axiosInstance.request<T>(config);
  return response.data;
};

/**
 * Helper functions for API calls
 */
export async function apiGet<T>(endpoint: string): Promise<T> {
  const response = await axiosInstance.get<T>(endpoint);
  return response.data;
}

export async function apiPost<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await axiosInstance.post<T>(endpoint, data);
  return response.data;
}

export async function apiPut<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await axiosInstance.put<T>(endpoint, data);
  return response.data;
}

export async function apiDelete(endpoint: string): Promise<void> {
  await axiosInstance.delete(endpoint);
}

export async function apiPatch<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await axiosInstance.patch<T>(endpoint, data);
  return response.data;
}

export async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  endpoint: string,
  data?: unknown
): Promise<T> {
  const response = await axiosInstance.request<T>({
    method,
    url: endpoint,
    data,
  });
  return response.data;
}

export default axiosInstance;
